import {
  type DashboardChannelArtifactResponse,
  type DashboardChannelArtifactSectionResponse,
  type DashboardChannelArtifactVersionsResponse,
  type DashboardChannelArtifactsResponse,
  listRetainedArtifactsForAChannelAsUser,
  listRetainedChannelArtifactVersionsAsUser,
  readARetainedChannelArtifactAsUser,
  readARetainedChannelArtifactSectionAsUser,
} from "../../dashboard-client";

// ---------------------------------------------------------------------------
// Channel-scoped artifact reads.
//
// Artifacts belong to the CHANNEL, not to whichever actor happened to write
// them. The old instance-scoped routes required a current actor membership, so
// a memberless / archived / retained-history channel appeared to have no
// artifacts at all — the durable record of the room died with its last actor.
// These routes authorize on organization ownership of the channel, so the
// record outlives every actor that produced it.
//
// Every operation here is a READ. There is no channel-scoped artifact write
// surface, which is what makes an inactive channel's artifacts safe to render:
// read-only is a property of the route set, not a UI flag that could drift.
// ---------------------------------------------------------------------------

export type ChannelArtifactSummary =
  DashboardChannelArtifactsResponse["artifacts"][number];
export type ChannelArtifactDetail = DashboardChannelArtifactResponse;
export type ChannelArtifactSectionToc =
  DashboardChannelArtifactResponse["sections"][number];
export type ChannelArtifactSectionBody =
  DashboardChannelArtifactSectionResponse;
export type ChannelArtifactVersion =
  DashboardChannelArtifactVersionsResponse["versions"][number];

/**
 * Transport overrides forwarded to the generated client. Production passes
 * nothing (the module-level client carries `credentials: "include"` from
 * openapi-ts.dashboard.runtime); tests inject `fetch` + `baseUrl` so the real
 * generated request path — URL construction included — is exercised without a
 * server.
 */
export interface ChannelArtifactTransport {
  baseUrl?: string;
  fetch?: typeof globalThis.fetch;
}

export async function fetchChannelArtifacts(
  channelId: string,
  transport: ChannelArtifactTransport = {},
): Promise<ChannelArtifactSummary[]> {
  const { data } = await listRetainedArtifactsForAChannelAsUser({
    ...transport,
    path: { channelId },
  });
  return data.artifacts;
}

export async function fetchChannelArtifactDetail(
  channelId: string,
  artifactId: string,
  transport: ChannelArtifactTransport = {},
): Promise<ChannelArtifactDetail> {
  const { data } = await readARetainedChannelArtifactAsUser({
    ...transport,
    path: { artifactId, channelId },
  });
  return data;
}

export async function fetchChannelArtifactVersions(
  channelId: string,
  artifactId: string,
  transport: ChannelArtifactTransport = {},
): Promise<ChannelArtifactVersion[]> {
  const { data } = await listRetainedChannelArtifactVersionsAsUser({
    ...transport,
    path: { artifactId, channelId },
  });
  return data.versions;
}

/**
 * Section reads are versioned. The body belongs to one artifact version, and
 * the pane displays and downloads whole documents, so an unversioned read would
 * let a concurrent edit swap one section under the others.
 */
export async function fetchChannelArtifactSection(
  channelId: string,
  artifactId: string,
  sectionId: string,
  version: number,
  transport: ChannelArtifactTransport = {},
): Promise<ChannelArtifactSectionBody> {
  const { data } = await readARetainedChannelArtifactSectionAsUser({
    ...transport,
    path: { artifactId, channelId, sectionId },
    query: { version },
  });
  return data;
}

/**
 * Assemble the artifact's markdown source: head + section bodies, in the
 * artifact's own section order. Section bodies are stored WITHOUT their
 * heading (the `## title` lives in the section record, exactly as the actor
 * read tool renders it), so the headings are re-emitted here; body text itself
 * is copied verbatim. Sections already loaded by the detail pane are reused.
 *
 * Every read — cached or fetched — is bound to `detail.current_version`. A
 * downloaded file must be one artifact version that actually existed, not a
 * blend of a cached older body with newly fetched ones.
 */
export async function fetchChannelArtifactMarkdown(
  channelId: string,
  detail: ChannelArtifactDetail,
  cachedBodies: Readonly<Record<string, ChannelArtifactSectionBody>> = {},
  transport: ChannelArtifactTransport = {},
): Promise<string> {
  const version = detail.current_version;
  const sections = await Promise.all(
    detail.sections.map(async (section) => {
      const cached = cachedBodies[section.id];
      if (cached?.artifact_version === version) {
        return cached;
      }
      return await fetchChannelArtifactSection(
        channelId,
        detail.id,
        section.id,
        version,
        transport,
      );
    }),
  );
  const mismatched = sections.find(
    (section) => section.artifact_version !== version,
  );
  if (mismatched) {
    // The server answered for a different version than the head we rendered.
    // Refusing is the only honest option: there is no coherent document here.
    throw new Error(
      `channel artifact version drift: expected v${version}, section '${mismatched.section_id}' is v${mismatched.artifact_version}`,
    );
  }
  return renderArtifactMarkdown(detail, sections);
}

/** Pure renderer — kept apart from the fetch so the file shape is testable. */
export function renderArtifactMarkdown(
  head: { lead: string | null; title: string },
  sections: readonly { body: string; title: string }[],
): string {
  const blocks: string[] = [`# ${head.title}`];
  if (head.lead) {
    blocks.push(head.lead);
  }
  for (const section of sections) {
    blocks.push(`## ${section.title}`, section.body);
  }
  const text = blocks.join("\n\n");
  return text.endsWith("\n") ? text : `${text}\n`;
}

/** Slugified title (falling back to the artifact slug) plus `.md`. */
export function artifactMarkdownFilename(artifact: {
  slug: string;
  title: string;
}): string {
  const base =
    slugifyForFilename(artifact.title) ||
    slugifyForFilename(artifact.slug) ||
    "artifact";
  return `${base}.md`;
}

function slugifyForFilename(value: string): string {
  return value
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, "-")
    .replaceAll(/^-+|-+$/g, "")
    .slice(0, 80)
    .replaceAll(/-+$/g, "");
}

/** Newest edit first — the list is a "what moved recently" surface. */
export function sortArtifactsByRecency(
  artifacts: readonly ChannelArtifactSummary[],
): ChannelArtifactSummary[] {
  return artifacts.toSorted(
    (a, b) =>
      new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
  );
}

export function countArtifactsByChannel(
  artifacts: readonly ChannelArtifactSummary[],
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const artifact of artifacts) {
    counts.set(artifact.channel_id, (counts.get(artifact.channel_id) ?? 0) + 1);
  }
  return counts;
}
